import PageLayout from '../../wrappers/PageLayout';
import './SellerDashboard.scss';
import Integrations from '../../components/Integrations/Integrations';
import { useEffect, useMemo, useState } from 'react';
import { Amount, SellerFilingReportToConfirm } from '../../core/types';
import {
  getClientReports,
  getCompletedFiling,
  getEasybillTokenStatus,
  getInProgressClientVATIds,
  getOngoingFiling,
  getPayments90days,
  getRegisteredVATIds,
  getStopMailing,
  getYearPayments,
  postClientAmazonConnect,
} from '../../core/api';
import { Loader } from '../../components/Loader/Loader';
import { useHistory, useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector, useQueriesTyped } from '../../hooks';
import { addNotification } from '../../core/store/notificationsSlice';
import { useTranslation } from 'react-i18next';
import { formatNumber, getRandomColor, getUTCDate } from '../../core/helpers';
import Participants from '../../components/Participants/Participants';
import { Cell, Pie, PieChart, ResponsiveContainer } from 'recharts';
import { useSellerIntegrations } from '../../context/integrations-context';
import GreenAlert from '../../components/GreenAlert/GreenAlert';
import SellerVATIdsOverview from './SellerVATIdsOverview';
import SellerCalendarCollapse from './SellerCalendarCollapse';
import classNames from 'classnames';
import FreshUserTodo from './FreshUserTodo';
import OpenTasks from '../../components/OpenTasks/OpenTasks';
import SellerChart from './SellerChart/SellerChart';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { usePP } from '../../context/pp-context';
import { useMU } from '../../context/mu-context';
import { ManualUploadFileStatusEnum } from '../../context/useManualUpload';
import MerryChristmasBanner from '../../components/MerryChristmasBanner';
import { differenceInCalendarMonths } from 'date-fns';
import { normalizeDateFE } from '../../core/normalization';

export const STATE_CONSTANT = 'customer-app';

export default function SellerDashboard() {
  const activeCompany = useAppSelector((s) => s.user.activeCompany);

  const { t } = useTranslation();
  const { vs3487PaymentsChart, vs3776BillbeeToken } = useFlags();

  const [isConnectingAmazon, setIsConnectingAmazon] = useState(false);
  const history = useHistory();
  const locaton = useLocation();
  const params = new URLSearchParams(locaton.search);
  const state = params.get('state');
  const spapi_oauth_code = params.get('spapi_oauth_code');
  const selling_partner_id = params.get('selling_partner_id');
  const user = useAppSelector((s) => s.user.user);
  const companies = useAppSelector((s) => s.user.companies);
  const isFreshUser = useAppSelector((s) => s.user.fresh);
  const dispatch = useAppDispatch();

  const getCountryLabel = (c: string) => {
    return c === 'OSS' ? 'OSS' : t(c);
  };

  const [
    registrations,
    registeredVATIds,
    reports,
    payments,
    manualUploadMailingStopped,
    ongoingFiling,
    completedFiling,
    easybill,
    yearPayments,
  ] = useQueriesTyped([
    {
      queryKey: ['SellerVATIds', user!.Id, activeCompany?.company_id],
      queryFn: () => getInProgressClientVATIds(activeCompany?.company_id),
    },
    {
      queryKey: ['SellerRegisteredVATIds', user!.Id, activeCompany?.company_id],
      queryFn: () => getRegisteredVATIds(activeCompany?.company_id),
    },
    {
      queryKey: ['SellerReports', user!.Id, activeCompany?.company_id],
      queryFn: () => getClientReports(activeCompany?.company_id),
    },
    {
      queryKey: ['SellerPayments90Days', user!.Id, activeCompany?.company_id],
      queryFn: () => getPayments90days(activeCompany?.company_id),
    },

    {
      queryKey: [
        'SellerStopManualUploadMailing',
        user!.Id,
        activeCompany?.company_id,
      ],
      queryFn: () =>
        getStopMailing('manual-upload-needed', activeCompany?.company_id),
    },
    {
      queryKey: ['SellerOngoingFiling', user!.Id, activeCompany?.company_id],
      queryFn: () => getOngoingFiling(activeCompany?.company_id),
    },
    {
      queryKey: ['SellerCompletedFiling', user!.Id, activeCompany?.company_id],
      queryFn: () => getCompletedFiling(activeCompany?.company_id),
    },
    {
      queryKey: ['SellerEasybill', user!.Id, activeCompany?.company_id],
      queryFn: () => getEasybillTokenStatus(activeCompany?.company_id),
    },
    {
      queryKey: ['SellerYearPayments', user!.Id, activeCompany?.company_id],
      queryFn: () => getYearPayments(activeCompany?.company_id),
    },
  ]);

  const {
    isLoading: isLoadingIntegrations,
    integrations,
    refetchIntegrations,
  } = useSellerIntegrations();

  const { isLoadingMU, uploadedFilesMU } = useMU();

  const { countPP, isLoadingPP } = usePP();

  useEffect(() => {
    if (
      state &&
      state.includes(STATE_CONSTANT) &&
      spapi_oauth_code &&
      selling_partner_id
    ) {
      const [user_id, company_id] = state
        .replace(STATE_CONSTANT, '')
        .split('.');
      if (
        companies.find((c) => c.company_id === company_id) &&
        user_id === user?.Id
      ) {
        setIsConnectingAmazon(true);
        postClientAmazonConnect({
          seller_id: selling_partner_id,
          oauth_code: spapi_oauth_code,
          company_id,
        })
          .then(() => {
            dispatch(
              addNotification({
                text: t('Merchant token *seller_id* connected').replace(
                  '*seller_id*',
                  selling_partner_id
                ),
                type: 'success',
              })
            );
          })
          .catch(() => {
            dispatch(
              addNotification({
                text: t('Something went wrong, please try again'),
                type: 'error',
              })
            );
          })
          .finally(() => {
            setIsConnectingAmazon(false);
            refetchIntegrations();
            history.replace('/');
          });
      }
    }

    // eslint-disable-next-line
  }, [state, spapi_oauth_code, selling_partner_id]);

  const paymentsData = payments.data;

  const vatPaid = useMemo(() => {
    if (!paymentsData) return 0;
    return (
      paymentsData.reduce((acc: number, item) => {
        const amount = item.Amounts.find(
          (a) => a.currency.toUpperCase() === 'EUR'
        );
        if (amount && amount.amount > 0) return acc + amount.amount;
        return acc;
      }, 0) || 0
    );
  }, [paymentsData]);

  const normalizedPayments: ChartItem[] = useMemo(() => {
    const OSS = {
      name: 'OSS',
      value: (paymentsData?.filter((p) => p.type === 'oss') || []).reduce(
        (acc, item) => {
          const amount = item.Amounts.find(
            (a) => a.currency.toUpperCase() === 'EUR'
          );
          return amount ? acc + amount.amount : acc;
        },
        0
      ),
    };

    const localPayments = (paymentsData || []).filter(
      (p) => p.type === 'local'
    );

    const paymentsByCountry = localPayments.reduce(
      (acc: { [key: string]: Amount[] }, item) => {
        acc[item.Country.short_name] = [
          ...(acc[item.Country.short_name] || []),
          ...item.Amounts,
        ];
        return acc;
      },
      {}
    );

    const normalizedLocalPayments: ChartItem[] = localPayments
      .filter((value, index, self) => {
        return (
          self.findIndex(
            (v) => v.Country.short_name === value.Country.short_name
          ) === index
        );
      })
      .map((p) => {
        const amounts = paymentsByCountry[p.Country.short_name].reduce(
          (acc: { [key: string]: number }, item) => {
            acc[item.currency] = (acc[item.currency] || 0) + item.amount;
            return acc;
          },
          {}
        );

        return {
          name: p.Country.short_name,
          originalCurrency: p.original_currency,
          value: amounts['EUR'],
          amounts,
        };
      });

    return [OSS, ...normalizedLocalPayments].filter((p) => p.value !== 0);
  }, [paymentsData]);

  const chartData = useMemo(
    () => (normalizedPayments || []).filter((p) => p.value > 0),
    [normalizedPayments]
  );

  const palette = useMemo(() => {
    return normalizedPayments.reduce(
      (
        acc: {
          [key: string]: string;
        },
        c
      ) => {
        acc[c.name] = palleteMap[c.name] || getRandomColor();
        return acc;
      },
      {}
    );
  }, [normalizedPayments]);

  const reportsData = reports.data;

  const hasProcessedFiles = uploadedFilesMU.some(
    (f) => f.status !== ManualUploadFileStatusEnum.CREATED && !f.errors.length
  );

  const ongoingFilingData = ongoingFiling.data;
  const completedFilingData = completedFiling.data;
  const reportsToConfirmPayment: SellerFilingReportToConfirm[] = useMemo(
    () =>
      [...(ongoingFilingData || []), ...(completedFilingData || [])].filter(
        (r) =>
          !!r.payment_instruction &&
          !r.payment_instruction.confirmed &&
          !r.payment_instruction.is_sepa &&
          !r.payment_instruction.is_null_report &&
          r.payment_instruction.amount.amount > 0
      ),
    [ongoingFilingData, completedFilingData]
  );

  const reportsToConfirmAgenda: SellerFilingReportToConfirm[] = useMemo(
    () =>
      [...(ongoingFilingData || []), ...(completedFilingData || [])].filter(
        (r) =>
          !!r.payment_instruction &&
          r.country.short_name === 'DE' &&
          !r.agenda_confirmed &&
          r.type === 'local'
      ),
    [ongoingFilingData, completedFilingData]
  );

  const isMUNeeded =
    activeCompany?.ManualUploadNeeded &&
    !hasProcessedFiles &&
    !manualUploadMailingStopped.data;

  const hasNoTodos =
    !isMUNeeded &&
    !countPP &&
    !reportsToConfirmPayment.length &&
    !reportsToConfirmAgenda.length;

  const hasOngoingTasks = useMemo(
    () =>
      isMUNeeded ||
      !!countPP ||
      [
        ...(reportsToConfirmPayment || []),
        ...(reportsToConfirmAgenda || []),
      ].filter(
        (r) =>
          differenceInCalendarMonths(
            getUTCDate(),
            normalizeDateFE(r.payment_instruction.due_date || r.deadline_date)
          ) <= 0
      ).length > 0,
    [countPP, isMUNeeded, reportsToConfirmAgenda, reportsToConfirmPayment]
  );

  const isLoading =
    registrations.isLoading ||
    registeredVATIds.isLoading ||
    reports.isLoading ||
    payments.isLoading ||
    manualUploadMailingStopped.isLoading ||
    isLoadingIntegrations ||
    isLoadingPP ||
    isLoadingMU ||
    ongoingFiling.isLoading ||
    completedFiling.isLoading ||
    easybill.isLoading ||
    yearPayments.isLoading;

  if (isConnectingAmazon) return <Loader />;

  return (
    <PageLayout
      top={<div className='Text--30 fw-900 mb-2'>{t('Global overview')}</div>}
      data-testid='seller-dashboard'
      className='flex-grow-1'
      rightColumn={
        <div
          style={{
            maxWidth: 328,
            paddingTop: hasOngoingTasks ? 32 : undefined,
          }}
          className='ms-lg-auto'
        >
          <MerryChristmasBanner />
          <div className='Paper mb-3 d-flex flex-column align-items-center justify-content-center py-4 px-3'>
            <div
              className='Text mb-1 fw-bold text-center mb-2 pb-1'
              data-testid='copmany-name'
            >
              {activeCompany?.name}
            </div>
            {user && <Participants participants={[user]} />}
          </div>
          <SellerCalendarCollapse reports={reportsData || []} />

          <div className='Paper ms-auto pb-3 pt-2 px-3 mb-3'>
            {(integrations ||
              vs3776BillbeeToken ||
              activeCompany?.EasybillClient) && (
              <Integrations
                integrations={integrations}
                easybillData={easybill.data}
              />
            )}
          </div>
        </div>
      }
    >
      <div>
        {isLoading && <Loader />}
        {isFreshUser && (
          <div className='Paper p-4 mb-3'>
            <div className='Text--sm text-uppercase mb-2'>
              {t('about client portal')}
            </div>
            <div className='Text--30 fw-900 mb-2'>
              👋 {t('Welcome')}, {user?.Name}
            </div>
            <div className='mb-3'>
              {t(
                "We appreciate your trust and we're working hard to improve your customer experience. We're preparing client portal to help you stay up to date. You'll be able to track what is going on and what is needed from you, when your reports will be submitted, how much and when you need to pay, and get access to all your documents."
              )}
            </div>
            <div className='row row-cols-auto gx-2'>
              {BADGES.map((b) => (
                <div className='col mb-2' key={b}>
                  <div className='SellerDashboard__badge px-1 rounded'>
                    {t(b)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        {hasNoTodos ? (
          <>{isFreshUser ? <FreshUserTodo /> : <GreenAlert />}</>
        ) : (
          <OpenTasks
            manualUploadNeeded={isMUNeeded}
            purchasePricesNeeded={typeof countPP === 'number' && countPP > 0}
            reportsToConfirmPayment={reportsToConfirmPayment}
            reportsToConfirmAgenda={reportsToConfirmAgenda}
          />
        )}
        <SellerVATIdsOverview
          inProgressData={registrations.data || []}
          registered={registeredVATIds.data || []}
        />
        {vs3487PaymentsChart !== undefined && (
          <>
            {vs3487PaymentsChart ? (
              <SellerChart
                payments={
                  yearPayments.data?.filter((a) => a.Amounts[0].amount > 0) ||
                  []
                }
                filingData={[
                  ...(ongoingFiling.data || []),
                  ...(completedFiling.data || []),
                ].filter((f) => !!f.payment_instruction)}
              />
            ) : (
              <div className='Paper px-4 pt-3 pb-4 mb-3'>
                {isFreshUser && (
                  <div className='fw-300'>
                    {t('Monitor your spendings over the last 90 days')}
                  </div>
                )}
                <div
                  className={classNames(
                    'd-flex justify-content-between fw-600',
                    {
                      'mb-n2': !normalizedPayments.length,
                    }
                  )}
                >
                  <span>
                    {t('VAT paid')}:{' '}
                    {formatNumber(+vatPaid / 100, {
                      currency: 'eur',
                    })}
                  </span>
                  <div>{t('Last 90 days')}</div>
                </div>
                {!!normalizedPayments.length && (
                  <div className='row row-cols-auto gx-3 pb-4'>
                    {normalizedPayments
                      .sort((a, b) => {
                        return b.value - a.value;
                      })
                      .map((item, index) => {
                        const amount =
                          item.originalCurrency &&
                          item.amounts?.[item.originalCurrency]
                            ? formatNumber(
                                Math.abs(
                                  item.amounts?.[item.originalCurrency] / 100
                                ),
                                {
                                  currency: item.originalCurrency,
                                }
                              )
                            : formatNumber(Math.abs(item.value / 100), {
                                currency: 'eur',
                              });

                        return (
                          <div className='col' key={`item-${index}`}>
                            <div className='d-flex align-items-center user-select-none'>
                              <div
                                className='SellerDashboard__circle rounded-circle me-1 flex-shrink-0'
                                style={{
                                  backgroundColor: palette[item.name],
                                }}
                              />
                              {item.value >= 0 ? (
                                <span>
                                  {getCountryLabel(item.name)} {amount}
                                </span>
                              ) : (
                                <span>
                                  {getCountryLabel(item.name)} {t('VAT Refund')}{' '}
                                  {amount}
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                  </div>
                )}
                {!!chartData.length && process.env.NODE_ENV !== 'test' && (
                  <div
                    className='d-flex flex-column'
                    style={{
                      height: 240,
                    }}
                  >
                    <ResponsiveContainer width='100%' height='100%'>
                      <PieChart>
                        <Pie
                          data={chartData}
                          cx='50%'
                          cy='50%'
                          labelLine={false}
                          outerRadius={120}
                          fill='#8884d8'
                          dataKey='value'
                          strokeWidth={0}
                        >
                          {chartData
                            ?.sort((a, b) => {
                              return b.value - a.value;
                            })
                            .map((c, index) => (
                              <Cell
                                key={`cell-${index}`}
                                fill={palette[c.name]}
                              />
                            ))}
                        </Pie>
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </PageLayout>
  );
}

const BADGES = [
  'VAT_setup_badge',
  'Filing',
  'Filing history',
  'Access to documents',
  'Multiple users',
  'Insights',
  'Updates & To Dos',
  'Payment instructions',
];

export interface ChartItem {
  name: string;
  value: number;
  amounts?: {
    [key: string]: number;
  };
  originalCurrency?: string;
}

export const palleteMap: {
  [key: string]: string;
} = {
  All: '#0052CD',
  OSS: '#1C3FFD',
  AT: '#7ECEFD',
  BE: '#2900CD',
  CZ: '#0092B2',
  DK: '#1BBC9B',
  FR: '#B1FF91',
  DE: '#FFEA00',
  IT: '#FFBE00',
  LU: '#FC7D49',
  NL: '#FFF176',
  PL: '#BEDB39',
  SI: '#43A047',
  ES: '#BDF271',
  SE: '#95AB63',
  GB: '#512DA8',
};
